import { CantileverButton } from 'components/CantileverButton'
import { X } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponentDeficiency,
  GeckoRiskAssessmentConfig,
  mapRiskClassToRiskScore,
} from 'types'
import { currencyFormatter } from 'utils/displayCurrency'
import {
  BusinessValueOption,
  calculateRiskAndConsequence,
  DEFAULT_BUSINESS_VALUE,
  getConsequenceThresholdFromBusinessValue,
  LIKELIHOOD_NUMBER_TO_TEXT,
  RiskCalculationMethod,
} from 'utils/fde/aiaFinancialConsequenceHelpers'

function getLikelihoodOptions(
  likelihoodLabels?: Array<{ description?: string }>
): Array<{ value: string; label: string }> {
  const baseOption = { value: '0', label: 'Select Likelihood' }

  if (likelihoodLabels) {
    const configOptions = likelihoodLabels.map((label, index) => ({
      value: (index + 1).toString(),
      label: label.description || `Level ${index + 1}`,
    }))
    return [baseOption, ...configOptions]
  }

  const defaultOptions = Object.entries(LIKELIHOOD_NUMBER_TO_TEXT).map(
    ([num, label]) => ({
      value: num,
      label,
    })
  )
  return [baseOption, ...defaultOptions]
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: #262626;
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 32rem;
  max-height: 80vh;
  overflow-y: auto;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`

const ModalTitle = styled.h3`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: ${({ theme }) => theme.forecast.defaultText};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`

const FormSection = styled.div`
  margin-bottom: 1rem;
`

const SectionTitle = styled.label`
  display: block;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`

const StyledSelect = styled.select`
  width: 100%;
  background-color: #363636;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;

  option {
    background-color: #363636;
    color: #ffffff;
  }

  &:focus {
    outline: none;
    border: 1px solid ${({ theme }) => theme.forecast.primary};
  }
`

const StyledInput = styled.input`
  width: 100%;
  background-color: #363636;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-family: inherit;
  box-sizing: border-box;

  &::placeholder {
    color: #898989;
  }

  &:focus {
    outline: none;
    border: 1px solid ${({ theme }) => theme.forecast.primary};
  }
`

const MatrixSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #404040;
`

const MatrixTitle = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
  text-align: center;
`

interface DeficiencyRiskSelectionModalProps {
  isOpen: boolean
  deficiency: GeckoComponentDeficiency | null
  onClose: () => void
  onSave: (
    riskScore: number,
    likelihood: number,
    consequence: number,
    financialImpact: number
  ) => void
  riskAssessmentConfig?: GeckoRiskAssessmentConfig
  businessValue?: BusinessValueOption
  RiskMatrixComponent: React.ComponentType<{
    likelihood?: number | null
    consequence?: number | null
    onCellClick: (
      value: string,
      likelihood: number,
      consequence: number
    ) => void
    readOnly?: boolean
  }>
}

export const DeficiencyRiskSelectionModal: React.FC<
  DeficiencyRiskSelectionModalProps
> = ({
  isOpen,
  deficiency,
  onClose,
  onSave,
  riskAssessmentConfig,
  businessValue = DEFAULT_BUSINESS_VALUE,
  RiskMatrixComponent,
}) => {
  const [tempRiskScore, setTempRiskScore] = useState(0)
  const [tempLikelihood, setTempLikelihood] = useState(0)
  const [tempConsequence, setTempConsequence] = useState(0)
  const [tempFinancialImpact, setTempFinancialImpact] = useState(0)

  const financialDisplayValue =
    tempFinancialImpact > 0 ? currencyFormatter.format(tempFinancialImpact) : ''

  useEffect(() => {
    if (deficiency) {
      setTempRiskScore(deficiency.data.riskScore || 0)
      setTempLikelihood(deficiency.data.likelihood || 0)
      setTempConsequence(deficiency.data.consequence || 0)
      setTempFinancialImpact(deficiency.data.financialImpact || 0)
    }
  }, [deficiency])

  const consequenceThreshold = useMemo(() => {
    return getConsequenceThresholdFromBusinessValue(businessValue)
  }, [businessValue])

  const likelihoodOptions = useMemo(
    () => getLikelihoodOptions(riskAssessmentConfig?.data.likelihoodLabels),
    [riskAssessmentConfig]
  )

  const calculateRisk = useCallback(
    async (likelihood: number, financialImpact: number) => {
      if (likelihood <= 0 || financialImpact <= 0) {
        setTempRiskScore(0)
        setTempConsequence(0)
        return
      }

      try {
        const result = await calculateRiskAndConsequence(
          riskAssessmentConfig?.data.riskCalculationMethod ||
            RiskCalculationMethod.DEFAULT,
          likelihood,
          financialImpact,
          {
            consequenceThreshold,
            riskMatrix: riskAssessmentConfig?.data.riskMatrix,
            riskMatrixOrientation:
              riskAssessmentConfig?.data.riskMatrixOrientation,
          }
        )

        const riskScore = mapRiskClassToRiskScore(
          result.riskClass,
          riskAssessmentConfig
        )
        setTempRiskScore(riskScore)
        if (result.consequencePosition) {
          setTempConsequence(result.consequencePosition)
        }
      } catch (error) {
        console.error('Error calculating risk:', error)
      }
    },
    [consequenceThreshold, riskAssessmentConfig]
  )

  useEffect(() => {
    calculateRisk(tempLikelihood, tempFinancialImpact)
  }, [tempLikelihood, tempFinancialImpact, calculateRisk])

  const handleLikelihoodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value) || 0
    setTempLikelihood(value)
  }

  const handleFinancialImpactChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '')
    setTempFinancialImpact(rawValue ? parseFloat(rawValue) : 0)
  }

  const handleSave = () => {
    onSave(tempRiskScore, tempLikelihood, tempConsequence, tempFinancialImpact)
  }

  return (
    <ModalOverlay $isOpen={isOpen}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            Set Risk - {deficiency?.data.title || 'Deficiency'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X />
          </CloseButton>
        </ModalHeader>

        <FormSection>
          <SectionTitle>Likelihood</SectionTitle>
          <StyledSelect
            value={tempLikelihood}
            onChange={handleLikelihoodChange}>
            {likelihoodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </StyledSelect>
        </FormSection>

        <FormSection>
          <SectionTitle>Evaluation Rationale ($)</SectionTitle>
          <StyledInput
            type='text'
            placeholder='Enter financial impact'
            value={financialDisplayValue}
            onChange={handleFinancialImpactChange}
          />
        </FormSection>

        <MatrixSection>
          <MatrixTitle>Risk Matrix (calculated from inputs)</MatrixTitle>
          <RiskMatrixComponent
            likelihood={tempLikelihood}
            consequence={tempConsequence}
            onCellClick={() => {}}
            readOnly={true}
          />
        </MatrixSection>

        <ModalActions>
          <CantileverButton onClick={onClose}>Cancel</CantileverButton>
          <CantileverButton
            primary
            disabled={tempLikelihood === 0 || tempFinancialImpact === 0}
            onClick={handleSave}>
            Save
          </CantileverButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  )
}
